import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Mail, Phone, Quote, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BookingPicker } from "@/components/BookingPicker";
import { SERVICES } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ETHOS_QUOTE_URL = "https://agents.ethoslife.com/invite/c31bd";

const inputCls =
  "w-full border border-white/15 bg-obsidian/60 px-4 py-3.5 text-sm text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-gold/60";

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
      {label}
    </label>
    {children}
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "Tax Preparation & Strategy",
    preferred_time: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener("va:preset-service", handler);
    return () => window.removeEventListener("va:preset-service", handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/consultations`, form);
      toast.success("Request received", {
        description: "Now pick your consultation time below.",
      });
      setSubmitted({ ...form });
      setForm({ name: "", email: "", phone: "", service: form.service, preferred_time: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong", {
        description: "Please call (770) 912-1516 or try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden bg-obsidian py-28 lg:py-40">
      <div className="absolute -right-40 top-20 h-[30rem] w-[30rem] rounded-full bg-[#2E1065]/40 blur-[150px]" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-5 lg:gap-20 lg:px-10">
        <div className="lg:col-span-2">
          <Reveal>
            <p className="eyebrow mb-6" data-testid="contact-eyebrow">Request Services</p>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Begin with a
              <span className="text-gold-gradient"> conversation.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/55">
              A confidential, no-obligation review of your taxes, protection, and legacy strategy.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12 space-y-5">
              <a href={ETHOS_QUOTE_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-quote-card"
                className="gold-border-glow flex items-center gap-5 bg-gold/10 p-5">
                <span className="flex h-11 w-11 items-center justify-center border border-gold/40 bg-gold/15 text-gold"><Quote size={17} /></span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gold/70">Instant Life Insurance Quote</span>
                  <span className="block font-display text-lg text-white">Get quoted in minutes →</span>
                </span>
              </a>
              <a href="tel:+17709121516" data-testid="contact-phone-card"
                className="gold-border-glow flex items-center gap-5 bg-wine/70 p-5">
                <span className="flex h-11 w-11 items-center justify-center border border-gold/30 text-gold"><Phone size={17} /></span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Call Direct</span>
                  <span className="block font-display text-lg text-white">+1 (770) 912-1516</span>
                </span>
              </a>
              <a href="mailto:robertaraehare.gfi@gmail.com" data-testid="contact-email-card"
                className="gold-border-glow flex items-center gap-5 bg-wine/70 p-5">
                <span className="flex h-11 w-11 items-center justify-center border border-gold/30 text-gold"><Mail size={17} /></span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Email</span>
                  <span className="block break-all text-sm text-white">robertaraehare.gfi@gmail.com</span>
                </span>
              </a>
              <div className="flex items-center gap-5 border border-white/10 bg-wine/40 p-5" data-testid="contact-response-card">
                <span className="flex h-11 w-11 items-center justify-center border border-white/15 text-white/60"><Clock size={17} /></span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Response Time</span>
                  <span className="block text-sm text-white/80">Within one business day</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-3">
          {submitted ? (
            <BookingPicker client={submitted} onDone={() => setSubmitted(null)} />
          ) : (
          <form onSubmit={submit} className="gold-border-glow bg-wine/70 p-8 sm:p-10" data-testid="consultation-form">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field label="Full Name" testid="field-name">
                <input required value={form.name} onChange={set("name")} placeholder="Your name"
                  className={inputCls} data-testid="input-name" />
              </Field>
              <Field label="Email" testid="field-email">
                <input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com"
                  className={inputCls} data-testid="input-email" />
              </Field>
              <Field label="Phone" testid="field-phone">
                <input value={form.phone} onChange={set("phone")} placeholder="(770) 000-0000"
                  className={inputCls} data-testid="input-phone" />
              </Field>
              <Field label="I Want To" testid="field-service">
                <select value={form.service} onChange={set("service")} className={inputCls} data-testid="select-service">
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title} className="bg-wine">{s.title}</option>
                  ))}
                  <option value="General Consultation" className="bg-wine">Request a General Consultation</option>
                  <option value="Join the Team" className="bg-wine">Join the Vision Aries Team</option>
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Preferred Date / Time" testid="field-time">
                  <input value={form.preferred_time} onChange={set("preferred_time")} placeholder="e.g. Weekday evenings, Saturday morning"
                    className={inputCls} data-testid="input-preferred-time" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Message" testid="field-message">
                  <textarea rows={4} value={form.message} onChange={set("message")}
                    placeholder="Tell us about your goals…" className={inputCls} data-testid="input-message" />
                </Field>
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="consultation-submit-button"
              className="group mt-8 flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-obsidian transition-all duration-300 hover:bg-gold-light disabled:opacity-60"
            >
              {sending ? "Sending…" : "Submit Request"}
              <Send size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
